import { LiveData, Store } from '@toeverything/infra';

import { recordBootEvent } from '../../../bootstrap/boot-trace';
import type { WorkspaceServerService } from '../../cloud';
import {
  deletePermissionSnapshotVault,
  getLocalAuthorityClientInstallId,
  readPermissionSnapshotVault,
  type SignedLocalAuthorityEnvelope,
  stableLocalAuthorityScopeHash,
  verifySignedLocalAuthorityEnvelope,
  writePermissionSnapshotVault,
} from '../../local-authority';
import type { GlobalState } from '../../storage';
import type { WorkspaceService } from '../../workspace';
import { resolveWorkspaceLocalAuthorityAccountId } from '../../workspace/utils/permission-cache-identity';

export interface PermissionSnapshotGrant {
  resourceId: string;
  resourceType: 'doc' | 'folder';
  permissions: Record<string, boolean>;
  role?: string;
  permissionSource?:
    | 'canonical-doc-permission-service'
    | 'canonical-folder-permission-service'
    | 'pending-local-owner';
  localMutationId?: string;
  trashed: boolean;
  updatedAt: string | null;
}

export interface SignedPermissionSnapshotGrant {
  resourceId: string;
  resourceType: 'doc' | 'folder';
  allowedActions: string[];
  role?: string;
  permissionSource:
    | 'canonical-doc-permission-service'
    | 'canonical-folder-permission-service';
  provenance: {
    sourceType: string;
    sourceId: string;
    viaGroupId: string | null;
    viaFolderId: string | null;
    inherited: boolean;
    effective: boolean;
    directOverridePresent: boolean;
  };
  trashed: boolean;
  updatedAt: string | null;
}

export interface PermissionSnapshotPayload {
  version: 1;
  subjectType: 'workspace-permission-snapshot';
  tenantId: string;
  workspaceId: string;
  userId: string;
  serverId: string;
  serverBaseUrlHash: string;
  clientInstallId: string;
  visibilityMode: 'user_effective' | 'workspace_admin' | 'system_audit';
  resourceScope: string;
  actionClass: string;
  actionTtlClass: 'read-view-48h';
  denyPrecedence: true;
  deviceBinding: 'bound' | 'degraded';
  permissionGraphVersion: number;
  resources: SignedPermissionSnapshotGrant[];
  issuedAt: string;
  expiresAt: string;
}

export interface PermissionSnapshotResponse {
  envelope: SignedLocalAuthorityEnvelope<
    Omit<PermissionSnapshotPayload, 'issuedAt' | 'expiresAt'>
  >;
}

export interface PermissionSnapshotScope {
  tenantId?: string | null;
  workspaceId: string;
  userId: string;
  serverId: string;
  serverBaseUrlHash: string;
  clientInstallId: string;
  visibilityMode: 'user_effective';
}

export class PermissionSnapshotStore extends Store {
  readonly grants$ = new LiveData<Record<string, PermissionSnapshotGrant>>({});
  readonly vaultStatus$ = new LiveData<LocalPermissionVaultStatus>('ready');

  private verifiedPermissionGraphVersion = 0;
  private verifiedTenantId: string | null = null;
  private observedPermissionGraphVersion = 0;
  private lastReportedStaleSnapshotKey: string | null = null;
  private hydration: Promise<void> | null = null;
  private readonly grantAuthority = new Map<string, GrantAuthority>();
  private readonly pendingLocalOwners = new Map<string, PendingLocalOwner>();
  private onlineResponseWrite: Promise<void> = Promise.resolve();
  private pendingLocalOwnerWrite: Promise<void> = Promise.resolve();

  constructor(
    private readonly workspaceServerService: WorkspaceServerService,
    private readonly workspaceService: WorkspaceService,
    private readonly globalState?: GlobalState
  ) {
    super();
  }

  getPermissionGraphVersion(): number {
    return this.verifiedPermissionGraphVersion;
  }

  getVerifiedTenantId(): string | null {
    return this.verifiedTenantId;
  }

  getDocPermissions(docId: string): Record<string, boolean> | null {
    return this.getActiveGrant('doc', docId)?.permissions ?? null;
  }

  getFolderPermissions(folderId: string): Record<string, boolean> | null {
    return this.getActiveGrant('folder', folderId)?.permissions ?? null;
  }

  getPermissionState(
    resourceType: 'doc' | 'folder',
    resourceId: string
  ): LocalPermissionGrantState {
    const key = grantKey(resourceType, resourceId);
    const grant = this.grants$.value[key];
    const authority = this.grantAuthority.get(key);
    const scope = this.currentScope();
    if (!grant || !authority) {
      const vaultStatus = this.vaultStatus$.value;
      return vaultStatus === 'locked'
        ? 'locked'
        : vaultStatus === 'corrupt'
          ? 'invalid'
          : vaultStatus === 'ready'
            ? 'unknown'
            : 'degraded';
    }
    if (authority.source === 'pending-local-owner') return 'pending-local';
    if (authority.expiresAt <= Date.now()) return 'expired';
    if (
      !scope ||
      authority.scopeFingerprint !== permissionScopeFingerprint(scope)
    ) {
      return 'invalid';
    }
    if (
      authority.permissionGraphVersion < this.observedPermissionGraphVersion
    ) {
      return 'stale';
    }
    return 'valid';
  }

  observePermissionGraphVersion(version: number): void {
    if (
      !Number.isFinite(version) ||
      version <= this.observedPermissionGraphVersion
    ) {
      return;
    }
    this.observedPermissionGraphVersion = version;
  }

  async hydrateLocal(): Promise<void> {
    if (!this.hydration) {
      this.hydration = this.hydrateLocalOnce().finally(() => {
        this.hydration = null;
      });
    }
    await this.hydration;
  }

  async applyOnlineResponse(
    response: PermissionSnapshotResponse
  ): Promise<void> {
    const next = this.onlineResponseWrite.then(() =>
      this.applyOnlineResponseSerial(response)
    );
    this.onlineResponseWrite = next.catch(error => {
      console.error('Failed to persist online permission snapshot:', error);
    });
    return next;
  }

  private async applyOnlineResponseSerial(
    response: PermissionSnapshotResponse
  ): Promise<void> {
    const scope = this.currentScope();
    if (!scope) {
      return;
    }
    const accepted = await this.hydrateSignedResponse(response, scope);
    if (!accepted) {
      return;
    }
    const scopedStorage = {
      ...scope,
      tenantId: accepted.tenantId,
    };
    const pointerKey = this.scopePointerKey(scope);
    const previousPointer =
      await readPermissionSnapshotVault<StoredPermissionSnapshotPointer>(
        pointerKey
      );
    const previousScopes =
      previousPointer.status === 'ready' && previousPointer.value?.version === 2
        ? previousPointer.value.resourceScopes
        : [];
    const resourceScopes = [
      accepted.resourceScope,
      ...previousScopes.filter(item => item !== accepted.resourceScope),
    ].slice(0, MAX_PERSISTED_PERMISSION_PARTITIONS);
    const evictedScopes = previousScopes.filter(
      item => !resourceScopes.includes(item)
    );
    const snapshotStatus = await writePermissionSnapshotVault(
      this.storageKey(scopedStorage, accepted.resourceScope),
      {
        cachedAt: Date.now(),
        response,
        version: 2,
      } satisfies StoredPermissionSnapshot
    );
    const pointerStatus = await writePermissionSnapshotVault(pointerKey, {
      tenantId: accepted.tenantId,
      permissionGraphVersion: accepted.permissionGraphVersion,
      resourceScopes,
      version: 2,
    } satisfies StoredPermissionSnapshotPointer);
    this.vaultStatus$.next(
      snapshotStatus === 'ready' && pointerStatus === 'ready'
        ? 'ready'
        : snapshotStatus === 'corrupt' || pointerStatus === 'corrupt'
          ? 'corrupt'
          : 'degraded'
    );
    await Promise.all(
      evictedScopes.map(resourceScope =>
        deletePermissionSnapshotVault(
          this.storageKey(scopedStorage, resourceScope)
        )
      )
    );
  }

  async hydrateSignedResponse(
    response: PermissionSnapshotResponse,
    scope: PermissionSnapshotScope
  ): Promise<PermissionSnapshotPayload | null> {
    const verification = await verifySignedLocalAuthorityEnvelope<
      Omit<PermissionSnapshotPayload, 'issuedAt' | 'expiresAt'>
    >(response.envelope, {
      expectedPurpose: 'permission-grant',
    });
    if (verification.status !== 'valid' || !verification.payload) {
      this.reportRejectedSnapshot(`verification:${verification.status}`);
      return null;
    }
    const payload = verification.payload;
    if (payload.permissionGraphVersion < this.observedPermissionGraphVersion) {
      this.reportRejectedSnapshot('permission-graph-stale', {
        observedPermissionGraphVersion: this.observedPermissionGraphVersion,
        permissionGraphVersion: payload.permissionGraphVersion,
      });
      return null;
    }
    const contractFailures = [
      payload.subjectType !== 'workspace-permission-snapshot'
        ? 'subject-type'
        : null,
      payload.workspaceId !== scope.workspaceId ? 'workspace' : null,
      payload.userId !== scope.userId ? 'user' : null,
      scope.tenantId && payload.tenantId !== scope.tenantId ? 'tenant' : null,
      payload.serverId !== scope.serverId ? 'server' : null,
      payload.serverBaseUrlHash !== scope.serverBaseUrlHash
        ? 'server-base-url'
        : null,
      payload.clientInstallId !== scope.clientInstallId
        ? 'client-install'
        : null,
      payload.visibilityMode !== scope.visibilityMode
        ? 'visibility-mode'
        : null,
      payload.denyPrecedence !== true ? 'deny-precedence' : null,
      payload.actionTtlClass !== 'read-view-48h' ? 'action-ttl' : null,
      !payload.resources.every(isSignedPermissionSnapshotGrant)
        ? 'resources'
        : null,
    ].filter((failure): failure is string => failure !== null);
    if (contractFailures.length > 0) {
      this.reportRejectedSnapshot('contract', {
        contractFailures,
      });
      return null;
    }
    this.verifiedPermissionGraphVersion = Math.max(
      this.verifiedPermissionGraphVersion,
      payload.permissionGraphVersion
    );
    this.verifiedTenantId = payload.tenantId;
    this.observedPermissionGraphVersion = Math.max(
      this.observedPermissionGraphVersion,
      payload.permissionGraphVersion
    );
    const grants = payload.resources.map(materializeSignedGrant);
    this.mergeGrants(grants, {
      expiresAt: Date.parse(payload.expiresAt),
      permissionGraphVersion: payload.permissionGraphVersion,
      scopeFingerprint: permissionScopeFingerprint(scope),
      source: 'signed',
    });
    let pendingOwnersChanged = false;
    for (const grant of grants) {
      pendingOwnersChanged =
        this.pendingLocalOwners.delete(
          grantKey(grant.resourceType, grant.resourceId)
        ) || pendingOwnersChanged;
    }
    if (pendingOwnersChanged) {
      await this.queuePendingLocalOwnerPersist(scope);
    }
    return payload;
  }

  applyPendingLocalOwner(docId: string, localMutationId: string): void {
    this.applyPendingLocalResourceOwner('doc', docId, localMutationId);
  }

  applyPendingLocalResourceOwner(
    resourceType: 'doc' | 'folder',
    resourceId: string,
    localMutationId: string
  ): void {
    const scope = this.currentScope();
    if (!scope) {
      return;
    }
    const key = grantKey(resourceType, resourceId);
    this.pendingLocalOwners.set(key, {
      resourceId,
      resourceType,
      localMutationId,
      createdAt: Date.now(),
    });
    this.mergeGrants(
      [
        {
          resourceId,
          resourceType,
          permissions:
            resourceType === 'doc'
              ? ownerDocPermissions()
              : ownerFolderPermissions(),
          permissionSource: 'pending-local-owner',
          localMutationId,
          trashed: false,
          updatedAt: null,
        },
      ],
      {
        expiresAt: Date.now() + PENDING_LOCAL_OWNER_TTL_MS,
        permissionGraphVersion: Number.MAX_SAFE_INTEGER,
        scopeFingerprint: permissionScopeFingerprint(scope),
        source: 'pending-local-owner',
      }
    );
    void this.queuePendingLocalOwnerPersist(scope);
  }

  async rejectPendingLocalOwner(
    docId: string,
    expectedLocalMutationId?: string
  ): Promise<boolean> {
    return this.rejectPendingLocalResourceOwner(
      'doc',
      docId,
      expectedLocalMutationId
    );
  }

  async rejectPendingLocalResourceOwner(
    resourceType: 'doc' | 'folder',
    resourceId: string,
    expectedLocalMutationId?: string
  ): Promise<boolean> {
    const scope = this.currentScope();
    const key = grantKey(resourceType, resourceId);
    const pending = this.pendingLocalOwners.get(key);
    if (
      !scope ||
      !pending ||
      (expectedLocalMutationId !== undefined &&
        pending.localMutationId !== expectedLocalMutationId)
    ) {
      return false;
    }

    this.pendingLocalOwners.delete(key);
    const grant = this.grants$.value[key];
    if (
      grant?.permissionSource === 'pending-local-owner' &&
      grant.localMutationId === pending.localMutationId
    ) {
      this.removeGrant(key);
    }
    await this.queuePendingLocalOwnerPersist(scope);
    return true;
  }

  private mergeGrants(
    grants: PermissionSnapshotGrant[],
    authority: GrantAuthority
  ): void {
    if (grants.length === 0) {
      return;
    }
    const next = { ...this.grants$.value };
    for (const grant of grants) {
      const key = grantKey(grant.resourceType, grant.resourceId);
      next[key] = grant;
      this.grantAuthority.set(key, authority);
    }
    this.grants$.next(next);
  }

  private async hydrateLocalOnce(): Promise<void> {
    const startedAt = performance.now();
    let outcome = 'missing-scope';
    let partitionCount = 0;
    let validPartitions = 0;
    recordBootEvent('permission-cache-hydration-started');
    const scope = this.currentScope();
    if (!scope) {
      recordBootEvent('permission-cache-hydration-finished', {
        durationMs: performance.now() - startedAt,
        outcome,
        partitionCount,
        validPartitions,
      });
      return;
    }
    outcome = 'error';
    try {
      const pendingStartedAt = performance.now();
      await this.hydratePendingLocalOwners(scope);
      recordBootEvent('permission-cache-pending-owners-ready', {
        durationMs: performance.now() - pendingStartedAt,
      });

      const pointerStartedAt = performance.now();
      const pointer =
        await readPermissionSnapshotVault<StoredPermissionSnapshotPointer>(
          this.scopePointerKey(scope)
        );
      partitionCount =
        pointer.status === 'ready' && pointer.value?.version === 2
          ? pointer.value.resourceScopes.length
          : 0;
      recordBootEvent('permission-cache-pointer-read', {
        durationMs: performance.now() - pointerStartedAt,
        partitionCount,
        status: pointer.status,
      });
      if (
        pointer.status !== 'ready' ||
        !pointer.value ||
        pointer.value.version !== 2
      ) {
        outcome =
          pointer.status === 'ready' ? 'missing-pointer' : pointer.status;
        if (pointer.status !== 'ready') this.vaultStatus$.next(pointer.status);
        return;
      }
      const scopedStorage = {
        ...scope,
        tenantId: pointer.value.tenantId,
      };
      let vaultReadTotalMs = 0;
      let vaultReadMaxMs = 0;
      let verifyTotalMs = 0;
      let verifyMaxMs = 0;
      const partitionsStartedAt = performance.now();
      const partitionResults = await mapWithConcurrency(
        pointer.value.resourceScopes,
        LOCAL_HYDRATION_CONCURRENCY,
        async resourceScope => {
          const key = this.storageKey(scopedStorage, resourceScope);
          const readStartedAt = performance.now();
          const stored =
            await readPermissionSnapshotVault<StoredPermissionSnapshot>(key);
          const readDurationMs = performance.now() - readStartedAt;
          vaultReadTotalMs += readDurationMs;
          vaultReadMaxMs = Math.max(vaultReadMaxMs, readDurationMs);
          if (stored.status !== 'ready') {
            this.vaultStatus$.next(stored.status);
            return false;
          }
          if (!stored.value || stored.value.version !== 2) return false;
          const verifyStartedAt = performance.now();
          const accepted = await this.hydrateSignedResponse(
            stored.value.response,
            {
              ...scope,
              tenantId: scopedStorage.tenantId,
            }
          );
          const verifyDurationMs = performance.now() - verifyStartedAt;
          verifyTotalMs += verifyDurationMs;
          verifyMaxMs = Math.max(verifyMaxMs, verifyDurationMs);
          if (!accepted || accepted.resourceScope !== resourceScope) {
            if (accepted) {
              this.reportRejectedSnapshot('resource-scope');
            }
            await deletePermissionSnapshotVault(key);
            return false;
          }
          return true;
        }
      );
      validPartitions = partitionResults.filter(Boolean).length;
      outcome = validPartitions > 0 ? 'restored' : 'empty';
      recordBootEvent('permission-cache-partitions-ready', {
        durationMs: performance.now() - partitionsStartedAt,
        partitionCount,
        validPartitions,
        vaultReadMaxMs,
        vaultReadTotalMs,
        verifyMaxMs,
        verifyTotalMs,
      });
      if (validPartitions === 0 && pointer.value.resourceScopes.length > 0) {
        await deletePermissionSnapshotVault(this.scopePointerKey(scope));
      }
    } finally {
      recordBootEvent('permission-cache-hydration-finished', {
        durationMs: performance.now() - startedAt,
        outcome,
        partitionCount,
        validPartitions,
      });
    }
  }

  private currentScope(): PermissionSnapshotScope | null {
    const server = this.workspaceServerService.server;
    const userId = server
      ? resolveWorkspaceLocalAuthorityAccountId({
          liveAccountId: server.confirmedAccountId,
          cachedAccountId: server.cachedAccountId,
          scopeReader: this.globalState,
          serverBaseUrl: server.baseUrl,
          serverId: server.id,
          workspaceId: this.workspaceService.workspace.id,
        })
      : null;
    if (!userId) {
      return null;
    }
    const serverBaseUrlHash = stableLocalAuthorityScopeHash(
      canonicalServerOrigin(server?.baseUrl ?? 'http://localhost:8080')
    );
    return {
      workspaceId: this.workspaceService.workspace.id,
      userId,
      serverId: `streamlain:${serverBaseUrlHash}`,
      serverBaseUrlHash,
      clientInstallId: getLocalAuthorityClientInstallId(),
      visibilityMode: 'user_effective',
    };
  }

  private storageKey(
    scope: PermissionSnapshotScope,
    resourceScope: string
  ): string {
    const server = this.workspaceServerService.server;
    return [
      'permission-snapshot',
      'v2',
      server?.id ?? 'local',
      server?.baseUrl ?? 'local',
      scope.tenantId ?? 'tenant-pending',
      scope.workspaceId,
      scope.userId,
      scope.clientInstallId,
      scope.visibilityMode,
      scope.serverBaseUrlHash,
      resourceScope,
    ].join(':');
  }

  private scopePointerKey(scope: PermissionSnapshotScope): string {
    return [
      'permission-snapshot-pointer',
      'v2',
      scope.serverId,
      scope.serverBaseUrlHash,
      scope.workspaceId,
      scope.userId,
      scope.clientInstallId,
      scope.visibilityMode,
    ].join(':');
  }

  private pendingLocalOwnerKey(scope: PermissionSnapshotScope): string {
    return [
      'permission-snapshot-pending-owner',
      'v2',
      scope.serverId,
      scope.serverBaseUrlHash,
      scope.workspaceId,
      scope.userId,
      scope.clientInstallId,
      scope.visibilityMode,
    ].join(':');
  }

  private async hydratePendingLocalOwners(
    scope: PermissionSnapshotScope
  ): Promise<void> {
    const stored =
      await readPermissionSnapshotVault<StoredPendingLocalOwnerGrants>(
        this.pendingLocalOwnerKey(scope)
      );
    if (stored.status !== 'ready' || stored.value?.version !== 2) {
      return;
    }

    const cutoff = Date.now() - PENDING_LOCAL_OWNER_TTL_MS;
    const grants: PermissionSnapshotGrant[] = [];
    for (const pending of stored.value.grants) {
      if (
        pending.createdAt < cutoff ||
        !pending.resourceId ||
        !pending.localMutationId
      ) {
        continue;
      }
      const key = grantKey(pending.resourceType, pending.resourceId);
      this.pendingLocalOwners.set(key, pending);
      grants.push({
        resourceId: pending.resourceId,
        resourceType: pending.resourceType,
        permissions:
          pending.resourceType === 'doc'
            ? ownerDocPermissions()
            : ownerFolderPermissions(),
        permissionSource: 'pending-local-owner',
        localMutationId: pending.localMutationId,
        trashed: false,
        updatedAt: null,
      });
    }
    for (const grant of grants) {
      const pending = this.pendingLocalOwners.get(
        grantKey(grant.resourceType, grant.resourceId)
      );
      if (!pending) continue;
      this.mergeGrants([grant], {
        expiresAt: pending.createdAt + PENDING_LOCAL_OWNER_TTL_MS,
        permissionGraphVersion: Number.MAX_SAFE_INTEGER,
        scopeFingerprint: permissionScopeFingerprint(scope),
        source: 'pending-local-owner',
      });
    }
    await this.queuePendingLocalOwnerPersist(scope);
  }

  private getActiveGrant(
    resourceType: 'doc' | 'folder',
    resourceId: string
  ): PermissionSnapshotGrant | null {
    const key = grantKey(resourceType, resourceId);
    const grant = this.grants$.value[key];
    const authority = this.grantAuthority.get(key);
    const scope = this.currentScope();
    if (
      !grant ||
      !authority ||
      !scope ||
      authority.expiresAt <= Date.now() ||
      authority.scopeFingerprint !== permissionScopeFingerprint(scope) ||
      (authority.source === 'signed' &&
        authority.permissionGraphVersion < this.observedPermissionGraphVersion)
    ) {
      if (scope && authority?.source === 'pending-local-owner') {
        this.pendingLocalOwners.delete(key);
        void this.queuePendingLocalOwnerPersist(scope);
      }
      this.removeGrant(key);
      return null;
    }
    return grant;
  }

  private removeGrant(key: string): void {
    if (!this.grants$.value[key] && !this.grantAuthority.has(key)) return;
    const next = { ...this.grants$.value };
    delete next[key];
    this.grantAuthority.delete(key);
    this.grants$.next(next);
  }

  private async persistPendingLocalOwners(
    scope: PermissionSnapshotScope
  ): Promise<void> {
    const key = this.pendingLocalOwnerKey(scope);
    if (this.pendingLocalOwners.size === 0) {
      await deletePermissionSnapshotVault(key);
      return;
    }
    await writePermissionSnapshotVault(key, {
      grants: [...this.pendingLocalOwners.values()],
      version: 2,
    } satisfies StoredPendingLocalOwnerGrants);
  }

  private queuePendingLocalOwnerPersist(
    scope: PermissionSnapshotScope
  ): Promise<void> {
    const next = this.pendingLocalOwnerWrite.then(() =>
      this.persistPendingLocalOwners(scope)
    );
    this.pendingLocalOwnerWrite = next.catch(error => {
      console.error('Failed to persist pending local ownership:', error);
    });
    return next;
  }

  private reportRejectedSnapshot(
    reason: string,
    details?: Record<string, unknown>
  ): void {
    if (reason === 'permission-graph-stale') {
      const staleSnapshotKey = `${String(
        details?.observedPermissionGraphVersion ?? 'unknown'
      )}:${String(details?.permissionGraphVersion ?? 'unknown')}`;
      if (staleSnapshotKey === this.lastReportedStaleSnapshotKey) {
        return;
      }
      this.lastReportedStaleSnapshotKey = staleSnapshotKey;
      console.debug('Discarded stale permission snapshot', {
        reason,
        ...details,
      });
      return;
    }
    console.error('Rejected cached permission snapshot', {
      reason,
      ...details,
    });
  }
}

interface StoredPermissionSnapshot {
  cachedAt: number;
  response: PermissionSnapshotResponse;
  version: 2;
}

interface GrantAuthority {
  expiresAt: number;
  permissionGraphVersion: number;
  scopeFingerprint: string;
  source: 'signed' | 'pending-local-owner';
}

interface StoredPermissionSnapshotPointer {
  tenantId: string;
  permissionGraphVersion: number;
  resourceScopes: string[];
  version: 2;
}

interface StoredPendingLocalOwnerGrants {
  grants: PendingLocalOwner[];
  version: 2;
}

interface PendingLocalOwner {
  resourceId: string;
  resourceType: 'doc' | 'folder';
  localMutationId: string;
  createdAt: number;
}

export type LocalPermissionVaultStatus =
  | 'ready'
  | 'unavailable'
  | 'locked'
  | 'corrupt'
  | 'unsupported'
  | 'degraded';

export type LocalPermissionGrantState =
  | 'valid'
  | 'stale'
  | 'unknown'
  | 'invalid'
  | 'expired'
  | 'locked'
  | 'degraded'
  | 'pending-local';

const PENDING_LOCAL_OWNER_TTL_MS = 48 * 60 * 60 * 1000;
const MAX_PERSISTED_PERMISSION_PARTITIONS = 64;
const LOCAL_HYDRATION_CONCURRENCY = 8;

async function mapWithConcurrency<T, R>(
  values: readonly T[],
  concurrency: number,
  mapper: (value: T) => Promise<R>
): Promise<R[]> {
  const results = new Array<R>(values.length);
  let nextIndex = 0;

  const workers = Array.from(
    { length: Math.min(concurrency, values.length) },
    async () => {
      while (nextIndex < values.length) {
        const index = nextIndex++;
        results[index] = await mapper(values[index]);
      }
    }
  );
  await Promise.all(workers);
  return results;
}

function ownerDocPermissions(): Record<string, boolean> {
  return {
    Doc_Comments_Create: true,
    Doc_Comments_Delete: true,
    Doc_Comments_Read: true,
    Doc_Comments_Resolve: true,
    Doc_Copy: true,
    Doc_Delete: true,
    Doc_Duplicate: true,
    Doc_Properties_Read: true,
    Doc_Properties_Update: true,
    Doc_Publish: true,
    Doc_Read: true,
    Doc_Restore: true,
    Doc_TransferOwner: true,
    Doc_Trash: true,
    Doc_Update: true,
    Doc_Users_Manage: true,
    Doc_Users_Read: true,
  };
}

function ownerFolderPermissions(): Record<string, boolean> {
  return {
    Folder_Read: true,
    Folder_Update: true,
    Folder_Delete: true,
    Folder_Trash: true,
    Folder_Restore: true,
    Folder_Move: true,
    Folder_AddDoc: true,
    Folder_RemoveDoc: true,
    Folder_CreateSubfolder: true,
    Folder_Users_Manage: true,
    Folder_Users_Read: true,
  };
}

function grantKey(resourceType: 'doc' | 'folder', resourceId: string): string {
  return `${resourceType}:${resourceId}`;
}

function permissionScopeFingerprint(scope: PermissionSnapshotScope): string {
  return [
    scope.workspaceId,
    scope.userId,
    scope.serverId,
    scope.serverBaseUrlHash,
    scope.clientInstallId,
    scope.visibilityMode,
  ].join(':');
}

function canonicalServerOrigin(value: string): string {
  try {
    return new URL(value).origin.toLowerCase();
  } catch {
    return value.trim().toLowerCase();
  }
}

function materializeSignedGrant(
  grant: SignedPermissionSnapshotGrant
): PermissionSnapshotGrant {
  return {
    resourceId: grant.resourceId,
    resourceType: grant.resourceType,
    permissions: Object.fromEntries(
      grant.allowedActions.map(action => [action, true])
    ),
    role: grant.role,
    permissionSource: grant.permissionSource,
    trashed: grant.trashed,
    updatedAt: grant.updatedAt,
  };
}

function isSignedPermissionSnapshotGrant(
  value: SignedPermissionSnapshotGrant
): boolean {
  return (
    typeof value.resourceId === 'string' &&
    value.resourceId.length > 0 &&
    (value.resourceType === 'doc' || value.resourceType === 'folder') &&
    Array.isArray(value.allowedActions) &&
    value.allowedActions.every(
      action =>
        typeof action === 'string' &&
        (action.startsWith('Doc_') || action.startsWith('Folder_'))
    ) &&
    typeof value.provenance?.sourceType === 'string' &&
    value.provenance.sourceType.length > 0 &&
    typeof value.provenance?.sourceId === 'string' &&
    value.provenance.sourceId.length > 0 &&
    typeof value.provenance.inherited === 'boolean' &&
    typeof value.provenance.effective === 'boolean' &&
    typeof value.provenance.directOverridePresent === 'boolean'
  );
}
